import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import API from '../../services/API';

const CheckInStatusPage = () => {
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]); // YYYY-MM-DD

    const fetchSummary = async (selectedDate = date) => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        setLoading(true);
        try {
            const res = await API.get(`/internal/attendance/summary?date=${selectedDate}`, { headers });

            if (res.data?.response_code === 1000) {
                setSchools(res.data.data || []);
            } else {
                toast.error(res.data?.message || 'Failed to fetch attendance summary', { autoClose: 5000 });
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch attendance summary.', { autoClose: 5000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();

        // 🔄 Auto refresh every 20s
        const interval = setInterval(() => {
            fetchSummary(date);
        }, 20000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const handleSearchChange = e => setSearchQuery(e.target.value);

    const filtered = schools.filter(s => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.trim().toUpperCase();
        return (
            (s.school_name || '').toUpperCase().includes(q) ||
            (s.school_id || '').toUpperCase().includes(q)
        );
    });

    // ✅ Calculate total check-in across all schools
    const totalCheckIns = filtered.reduce((sum, s) => sum + Number(s.total_check_in || 0), 0);

    const chip = (text, bg, fg) => (
        <span
            style={{
                backgroundColor: bg,
                color: fg,
                padding: '5px 10px',
                borderRadius: '12px',
                fontWeight: 'bold',
                display: 'inline-block',
                minWidth: 70,
                textAlign: 'center',
            }}
        >
            {text}
        </span>
    );

    const columns = [
        {
            name: 'School ID',
            selector: row => row.school_id,
            sortable: true,
            wrap: true,
            grow: 1.2,
        },
        {
            name: 'School Name',
            selector: row => row.school_name,
            sortable: true,
            wrap: true,
            grow: 2,
        },
        {
            id: 'totalCheckin',
            name: 'Total Check-in',
            selector: row => Number(row.total_check_in),   // force numeric
            cell: row => (
                <span
                    style={{
                        backgroundColor: '#007bff20',
                        color: '#0056b3',
                        padding: '5px 10px',
                        borderRadius: '12px',
                        fontWeight: '600',
                        display: 'inline-block',
                        minWidth: 80,
                        textAlign: 'center',
                    }}
                >
                    {row.total_check_in}
                </span>
            ),
            sortable: true,
            right: true,
            width: '160px',
        },
        {
            name: 'Recorded Moeis',
            selector: row => row.recorded_moeis_count,
            cell: row => chip(row.recorded_moeis_count, '#d4edda', '#155724'),
            sortable: true,
            right: true,
            width: '180px',
        }
    ];

    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card rounded-25">
                            <div className="card-header rounded-25 d-flex align-items-center justify-content-between">
                                <h5 className="mb-0">Attendance Summary (Schools)</h5>
                                <div className="d-flex gap-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={date}
                                        onChange={e => {
                                            setDate(e.target.value);
                                            fetchSummary(e.target.value);
                                        }}
                                        style={{ maxWidth: 200 }}
                                    />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by school name / ID"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        style={{ maxWidth: 320 }}
                                    />
                                    <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                                        Clear
                                    </button>
                                    <button className="btn btn-primary" onClick={() => fetchSummary(date)}>
                                        Refresh
                                    </button>
                                </div>
                            </div>

                            <div className="card-body">
                                {/* ✅ Total Check-in Summary */}
                                <div className="mb-3">
                                    <h6>
                                        Total Check-ins:{" "}
                                        <span className="badge bg-primary fs-6">
                                            {totalCheckIns}
                                        </span>
                                    </h6>
                                </div>

                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={filtered}
                                        striped
                                        persistTableHead
                                        progressPending={loading}
                                        defaultSortFieldId="totalCheckin"
                                        defaultSortAsc={false}   // highest first
                                    />
                                </div>
                                <small className="text-muted">
                                    Showing {filtered.length} schools for date {date} (Auto-refresh every 20s)
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default CheckInStatusPage;
